import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { Card, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from 'chart.js';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { useGetSubmissionStatsQuery } from '../../slices/adminApiSlice';
import { usePalette } from '../../hooks/usePalette';
import Loader from '../../components/Loader';
import Message from '../../components/Message';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const RANGES = [
  { value: '14', label: 'Last 2 weeks' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
];

const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });

const countryLabel = (code) => {
  if (!code || code === 'Unknown') return 'Unknown';
  try {
    return countryNames.of(code) || code;
  } catch {
    return code;
  }
};

// Regional-indicator emoji built from the ISO 3166-1 alpha-2 code
const countryFlag = (code) => {
  if (!/^[A-Z]{2}$/.test(code || '')) return '🌐';
  return String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
};

// Bucket labels are YYYY-MM-DD or YYYY-MM in the requested time zone; parse
// as UTC and format as UTC so the label isn't shifted by the browser offset.
const formatPeriod = (period, granularity) => {
  const [y, m, d] = period.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d || 1));
  return granularity === 'month'
    ? date.toLocaleDateString([], { month: 'short', year: 'numeric', timeZone: 'UTC' })
    : date.toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

export default function SubmissionStatsScreen() {
  const [range, setRange] = useState('14');
  const [expanded, setExpanded] = useState(() => new Set());

  const { userInfo } = useSelector((state) => state.auth);

  // Uploads arrive from the desktop client, which no cache tag tracks, so
  // always refetch when the page is opened
  const { data, isFetching, error } = useGetSubmissionStatsQuery(
    userInfo ? { range, tz: BROWSER_TZ } : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  const { palette } = usePalette();

  const chartData = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.uploadsOverTime.map((b) => formatPeriod(b.period, data.granularity)),
      datasets: [
        {
          label: 'Benchmarks uploaded',
          data: data.uploadsOverTime.map((b) => b.count),
          backgroundColor: palette?.readSample ? `${palette.readSample}B3` : 'rgba(54, 162, 235, 0.7)',
          borderColor: palette?.readSample || 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [data, palette]);

  const chartTitle = `Benchmarks Uploaded per ${data?.granularity === 'month' ? 'Month' : 'Day'}`;

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: {
        grid: { color: palette.gridColor, borderDash: [5, 5] },
        ticks: { color: palette.textColor },
      },
      y: {
        beginAtZero: true,
        grid: { color: palette.gridColor, borderDash: [5, 5] },
        ticks: { precision: 0, color: palette.textColor },
        title: { display: true, text: 'Uploads', color: palette.textColor },
      },
    },
  }), [palette]);

  if (!userInfo) return <Loader />;

  const renderError = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err.data?.message) return err.data.message;
    if (err.error) return err.error;
    try { return JSON.stringify(err); } catch { return String(err); }
  };

  const toggleCountry = (country) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(country)) next.delete(country);
      else next.add(country);
      return next;
    });
  };

  const percent = (count) =>
    data?.totalAudited ? `${((count / data.totalAudited) * 100).toFixed(1)}%` : '—';

  return (
    <>
      <Link
        to={`/admin/home`}
        className='btn btn-light'
        style={{ marginTop: '32px', marginBottom: '16px' }}
      >
        Return to Admin Home
      </Link>

      <h1 style={{ marginBottom: '16px' }}>Submission Stats</h1>

      <div className='d-flex flex-wrap gap-4 mb-4'>
        <div className='d-flex align-items-center'>
          <label htmlFor='submission-stats-range' className='me-2 fw-bold'>Period:</label>
          <select
            id='submission-stats-range'
            value={range}
            onChange={(e) => {
              setRange(e.target.value);
              setExpanded(new Set());
            }}
            className='form-select form-select--custom w-auto'
          >
            {RANGES.map((r) => (
              <option className='option--custom' key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
      </div>

      {isFetching && !data ? (
        <Loader />
      ) : error ? (
        <Message variant='danger'>{renderError(error)}</Message>
      ) : (
        <>
          <Card className='shadow-sm card--opaque mb-3'>
            <Card.Header as='h2' className='fw-bold fs-6 mb-0'>{chartTitle}</Card.Header>
            <Card.Body>
              <div className='mb-2 small text-muted'>
                {data.totalUploads} benchmark{data.totalUploads === 1 ? '' : 's'} uploaded
                {' '}(currently in database, {data.timeZone})
              </div>
              <div
                className='rounded'
                style={{ backgroundColor: palette.chartBg, padding: '0.75rem' }}
                aria-hidden='true'
              >
                <div style={{ height: '310px' }}>
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>
              <table className='visually-hidden'>
                <caption>{chartTitle}</caption>
                <thead>
                  <tr>
                    <th scope='col'>Period</th>
                    <th scope='col'>Uploads</th>
                  </tr>
                </thead>
                <tbody>
                  {data.uploadsOverTime.map((b) => (
                    <tr key={b.period}>
                      <th scope='row'>{formatPeriod(b.period, data.granularity)}</th>
                      <td>{b.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card.Body>
          </Card>

          <Card className='shadow-sm card--opaque mb-3'>
            <Card.Header as='h2' className='fw-bold fs-6 mb-0'>Submission Locations</Card.Header>
            <Card.Body>
              {data.countries.length === 0 ? (
                <Message variant='info'>
                  No recorded submissions for this period. Locations are recorded for uploads
                  received after submission auditing was enabled.
                </Message>
              ) : (
                <>
                  <Table borderless responsive size='sm' className='mb-0'>
                    <thead>
                      <tr>
                        <th style={{ width: '32px' }}></th>
                        <th>Country / Region</th>
                        <th className='text-end'>Submissions</th>
                        <th className='text-end'>Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.countries.map((c) => {
                        const isOpen = expanded.has(c.country);
                        const regionRowIds = c.regions.map((_, i) => `regions-${c.country}-${i}`);
                        return (
                          <Fragment key={c.country}>
                            <tr
                              onClick={() => toggleCountry(c.country)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>
                                <button
                                  type='button'
                                  className='btn btn-link p-0 text-reset'
                                  aria-expanded={isOpen}
                                  aria-controls={isOpen ? regionRowIds.join(' ') : undefined}
                                  aria-label={`${isOpen ? 'Hide' : 'Show'} regions for ${countryLabel(c.country)}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCountry(c.country);
                                  }}
                                >
                                  {isOpen ? <FaChevronDown aria-hidden='true' /> : <FaChevronRight aria-hidden='true' />}
                                </button>
                              </td>
                              <td className='fw-bold'>
                                <span className='me-2' aria-hidden='true'>{countryFlag(c.country)}</span>
                                {countryLabel(c.country)}
                                {c.country !== 'Unknown' && (
                                  <span className='text-muted fw-normal ms-2 small'>{c.country}</span>
                                )}
                              </td>
                              <td className='text-end'>{c.count}</td>
                              <td className='text-end'>{percent(c.count)}</td>
                            </tr>
                            {isOpen && c.regions.map((r, i) => (
                              <tr key={`${c.country}-${r.region}`} id={regionRowIds[i]} className='small'>
                                <td></td>
                                <td className='ps-4 text-muted'>{r.region}</td>
                                <td className='text-end text-muted'>{r.count}</td>
                                <td className='text-end text-muted'>{percent(r.count)}</td>
                              </tr>
                            ))}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </Table>
                  <div className='mt-2 small text-muted'>
                    {data.totalAudited} submission{data.totalAudited === 1 ? '' : 's'} recorded
                    {' · '}{data.totalLocated} located
                    {' · '}{data.totalAudited - data.totalLocated} unknown
                    <br />
                    Includes submissions whose benchmark was later deleted.
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </>
  );
}
